import React, { useState, useEffect } from 'react';
import { Toast } from '../ui.jsx';
import {
  apiLogin, apiTeacherLogin, apiParentLogin, getSessions,
} from '../storage';
import ParentRecovery from './ParentRecovery';
import TeacherRecovery from './TeacherRecovery';

const LOGO_SRC = process.env.PUBLIC_URL + '/logo.png';

function Login({ db, onLogin }) {
  const [role, setRole] = useState('parent');
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [latestYear, setLatestYear] = useState('');

  // Fetch latest session year to show on login page
  useEffect(() => {
    getSessions().then(list => {
      if (list.length) setLatestYear(list[list.length - 1].year || '');
    }).catch(() => { });
  }, []);

  // Recovery flow state — parent
  const [recoveryStep, setRecoveryStep] = useState(null);
  const [recoveryId, setRecoveryId] = useState('');
  const [recoveryOtp, setRecoveryOtp] = useState('');
  const [recoveryPw, setRecoveryPw] = useState('');
  const [recoveryPwConf, setRecoveryPwConf] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryErr, setRecoveryErr] = useState('');
  const [recoveryMaskedEmail, setRecoveryMaskedEmail] = useState('');

  // Recovery flow state — teacher
  const [tRecoveryStep, setTRecoveryStep] = useState(null);
  const [tRecoveryUser, setTRecoveryUser] = useState('');
  const [tRecoveryOtp, setTRecoveryOtp] = useState('');
  const [tRecoveryPw, setTRecoveryPw] = useState('');
  const [tRecoveryPwConf, setTRecoveryPwConf] = useState('');
  const [tRecoveryLoading, setTRecoveryLoading] = useState(false);
  const [tRecoveryErr, setTRecoveryErr] = useState('');
  const [tRecoveryMasked, setTRecoveryMasked] = useState('');

  const resetRecovery = () => { setRecoveryStep(null); setRecoveryId(''); setRecoveryOtp(''); setRecoveryPw(''); setRecoveryPwConf(''); setRecoveryErr(''); };
  const resetTRecovery = () => { setTRecoveryStep(null); setTRecoveryUser(''); setTRecoveryOtp(''); setTRecoveryPw(''); setTRecoveryPwConf(''); setTRecoveryErr(''); };

  const genCaptcha = () => {
    const a = Math.floor(Math.random() * 9) + 1;
    const b = Math.floor(Math.random() * 9) + 1;
    return { q: `${a} + ${b}`, ans: a + b };
  };
  const [captcha, setCaptcha] = useState(genCaptcha);
  const [captchaVal, setCaptchaVal] = useState('');
  const refreshCaptcha = () => { setCaptcha(genCaptcha()); setCaptchaVal(''); };

  useEffect(() => { const t = setTimeout(() => setMounted(true), 60); return () => clearTimeout(t); }, []);

  const signin = async () => {
    if (!u || !p) { setErr('Enter username and password'); return; }
    if (parseInt(captchaVal) !== captcha.ans) { setErr('Incorrect captcha answer'); refreshCaptcha(); return; }
    setLoading(true); setErr('');
    try {
      if (role === 'admin') {
        const user = await apiLogin(u, p);
        onLogin({ user, role: 'admin' });
      } else if (role === 'teacher') {
        const sessions = await getSessions();
        if (!sessions.length) { setErr('No sessions found. Create a session first.'); setLoading(false); return; }
        const latestSession = sessions[sessions.length - 1];
        const user = await apiTeacherLogin(u, p, latestSession.sid || latestSession.id);
        onLogin({ user, role: 'teacher' }, latestSession.sid || latestSession.id);
      } else {
        const user = await apiParentLogin(u, p);
        onLogin({ user: { ...user, role: 'parent' }, role: 'parent' });
      }
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('fetch') || msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('Load failed')) {
        setErr('Cannot reach server. Make sure the backend is running on port 5001.');
      } else {
        setErr(msg || 'Invalid credentials');
      }
    }
    setLoading(false);
  };

  const ORBS = [
    { w: 320, h: 320, top: '-80px', left: '-80px', color: 'rgba(255,255,255,0.04)', delay: '0s', dur: '18s' },
    { w: 200, h: 200, top: '40%', right: '-60px', color: 'rgba(255,255,255,0.06)', delay: '3s', dur: '14s' },
    { w: 140, h: 140, bottom: '80px', left: '30%', color: 'rgba(255,255,255,0.05)', delay: '6s', dur: '20s' },
    { w: 80, h: 80, top: '20%', left: '55%', color: 'rgba(255,255,255,0.08)', delay: '1s', dur: '10s' },
  ];

  const anim = (delay = 0) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'none' : 'translateY(18px)',
    transition: `opacity 600ms ${delay}ms cubic-bezier(0.4,0,0.2,1), transform 600ms ${delay}ms cubic-bezier(0.4,0,0.2,1)`,
  });

  const isStaff = role === 'admin' || role === 'teacher';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: '#f0f4ff', fontFamily: 'Inter,sans-serif' }}>
      <style>{`
        @keyframes lp-float{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-18px) scale(1.04)}}
        @keyframes lp-spin{to{transform:rotate(360deg)}}
        @keyframes lp-pulse{0%,100%{opacity:0.5}50%{opacity:1}}
        @keyframes lp-shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-5px)}40%,80%{transform:translateX(5px)}}
        @keyframes lp-slidein{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
        @keyframes lp-glow{0%,100%{opacity:0.6}50%{opacity:1}}
        .lp-input:focus{border-color:#1960a3!important;box-shadow:0 0 0 3px rgba(25,96,163,0.12)!important;background:#fff!important}
        .lp-input{transition:border-color 200ms,box-shadow 200ms,background 200ms}
        .lp-btn-main:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 12px 36px rgba(25,96,163,0.42)!important}
        .lp-btn-main:active:not(:disabled){transform:scale(0.98)}
        @media(max-width:900px){.lp-left-panel{display:none!important}}
        @media(max-width:600px){.lp-form-wrap{padding:20px 18px 28px!important}}
      `}</style>
      {/* Left panel */}
      <div className="lp-left-panel" style={{ flex: 1, background: 'linear-gradient(145deg,#001530 0%,#002045 55%,#0a3060 100%)', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '48px 52px' }}>
        {ORBS.map((o, i) => (
          <div key={i} style={{ position: 'absolute', width: o.w, height: o.h, top: o.top, left: o.left, right: o.right, bottom: o.bottom, borderRadius: '50%', background: o.color, animation: `lp-float ${o.dur} ${o.delay} ease-in-out infinite`, pointerEvents: 'none' }} />
        ))}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)', backgroundSize: '52px 52px', pointerEvents: 'none' }} />

        <div style={{ ...anim(0), position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 14, marginBottom: 64 }}>
          <img src={LOGO_SRC} alt="LKPS" style={{ width: 48, height: 48, objectFit: 'contain', filter: 'drop-shadow(0 4px 12px rgba(96,165,250,0.3))' }} onError={e => e.target.style.display = 'none'} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 900, color: '#fff', fontFamily: 'Manrope,sans-serif' }}>{db.settings.school || 'LORD KRISHNA PUBLIC SCHOOL'}</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.2em', textTransform: 'uppercase', marginTop: 2 }}>Mathura</div>
          </div>
        </div>

        <div style={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <div style={{ ...anim(80), display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 999, padding: '6px 14px', marginBottom: 22 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#60a5fa', animation: 'lp-glow 2s ease-in-out infinite' }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: '#93c5fd', letterSpacing: '0.12em', textTransform: 'uppercase' }}>{isStaff ? 'Staff Portal' : 'Parent Portal'}</span>
          </div>
          {isStaff ? <>
            <h1 style={{ ...anim(140), fontSize: 46, fontWeight: 900, color: '#fff', lineHeight: 1.08, letterSpacing: '-0.03em', margin: '0 0 18px', fontFamily: 'Manrope,sans-serif' }}>
              Manage your<br />
              <span style={{ background: 'linear-gradient(90deg,#60a5fa,#a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>school portal</span>
            </h1>
            <p style={{ ...anim(200), fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, maxWidth: 340, margin: '0 0 44px' }}>
              Access attendance, marks, timetable, and school management tools — all in one place.
            </p>
            <div style={{ ...anim(260), display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { icon: 'fact_check', label: 'Student Attendance', color: '#34d399' },
                { icon: 'quiz', label: 'Marks & Exams', color: '#a78bfa' },
                { icon: 'schedule', label: 'Timetable', color: '#60a5fa' },
                { icon: 'co_present', label: 'My Attendance', color: '#fb923c' },
              ].map(f => (
                <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 10, background: f.color + '18', border: `1px solid ${f.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: f.color, fontVariationSettings: "'FILL' 1" }}>{f.icon}</span>
                  </div>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>{f.label}</span>
                </div>
              ))}
            </div>
          </> : <>
            <h1 style={{ ...anim(140), fontSize: 46, fontWeight: 900, color: '#fff', lineHeight: 1.08, letterSpacing: '-0.03em', margin: '0 0 18px', fontFamily: 'Manrope,sans-serif' }}>
              Stay connected<br />
              <span style={{ background: 'linear-gradient(90deg,#60a5fa,#a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>with your child</span>
            </h1>
            <p style={{ ...anim(200), fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, maxWidth: 340, margin: '0 0 44px' }}>
              Track attendance, view marks, check fee status, and stay updated on school events — all in one place.
            </p>
            <div style={{ ...anim(260), display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { icon: 'fact_check', label: 'Real-time Attendance', color: '#34d399' },
                { icon: 'quiz', label: 'Exam Results & Marks', color: '#a78bfa' },
                { icon: 'payments', label: 'Fee Status & History', color: '#60a5fa' },
                { icon: 'calendar_month', label: 'School Calendar & Events', color: '#fb923c' },
              ].map(f => (
                <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 10, background: f.color + '18', border: `1px solid ${f.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: f.color, fontVariationSettings: "'FILL' 1" }}>{f.icon}</span>
                  </div>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>{f.label}</span>
                </div>
              ))}
            </div>
          </>}
        </div>

        <div style={{ ...anim(320), position: 'relative', zIndex: 1, paddingTop: 22, borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.22)', fontWeight: 600 }}>Session {latestYear || db.settings.year || '2026-27'}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {['#60a5fa', '#a78bfa', '#34d399'].map((c, i) => (
              <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: c, animation: `lp-pulse 2s ${i * 0.4}s ease-in-out infinite` }} />
            ))}
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div style={{ width: '100%', maxWidth: 500, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#fff', boxShadow: '-4px 0 40px rgba(0,32,69,0.1)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg,#1960a3,#60a5fa,#a78bfa)', zIndex: 3 }} />
        <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: 'rgba(25,96,163,0.04)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(167,139,250,0.05)', pointerEvents: 'none' }} />

        <div style={{ ...anim(0), padding: '24px 36px 0', position: 'relative', zIndex: 2 }}>
          <button onClick={() => {
            // Clear session so returning to the app requires fresh login
            try { localStorage.removeItem('lkps_user'); localStorage.removeItem('lkps_user_pic'); localStorage.removeItem('lkps_active_session'); } catch (e) { }
            try { sessionStorage.clear(); } catch (e) { }
            window.location.href = '/';
          }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 14px 7px 8px', borderRadius: 24, border: '1.5px solid rgba(25,96,163,0.15)', background: 'rgba(25,96,163,0.05)', color: '#1960a3', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 200ms', fontFamily: 'inherit' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(25,96,163,0.1)'; e.currentTarget.style.transform = 'translateX(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(25,96,163,0.05)'; e.currentTarget.style.transform = 'none'; }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'linear-gradient(135deg,#1960a3,#60a5fa)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 13, color: '#fff' }}>arrow_back</span>
            </div>
            Back to Website
          </button>
        </div>

        <div className="lp-form-wrap" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '24px 40px 32px', position: 'relative', zIndex: 2 }}>
          <div style={{ width: '100%', maxWidth: 380, margin: '0 auto' }}>

            {recoveryStep && <ParentRecovery
              step={recoveryStep} setStep={setRecoveryStep}
              identifier={recoveryId} setIdentifier={setRecoveryId}
              otp={recoveryOtp} setOtp={setRecoveryOtp}
              pw={recoveryPw} setPw={setRecoveryPw}
              pwConf={recoveryPwConf} setPwConf={setRecoveryPwConf}
              loading={recoveryLoading} setLoading={setRecoveryLoading}
              err={recoveryErr} setErr={setRecoveryErr}
              maskedEmail={recoveryMaskedEmail} setMaskedEmail={setRecoveryMaskedEmail}
              onBack={resetRecovery}
            />}

            {tRecoveryStep && <TeacherRecovery
              step={tRecoveryStep} setStep={setTRecoveryStep}
              username={tRecoveryUser} setUsername={setTRecoveryUser}
              otp={tRecoveryOtp} setOtp={setTRecoveryOtp}
              pw={tRecoveryPw} setPw={setTRecoveryPw}
              pwConf={tRecoveryPwConf} setPwConf={setTRecoveryPwConf}
              loading={tRecoveryLoading} setLoading={setTRecoveryLoading}
              err={tRecoveryErr} setErr={setTRecoveryErr}
              maskedEmail={tRecoveryMasked} setMaskedEmail={setTRecoveryMasked}
              onBack={resetTRecovery}
            />}

            {!recoveryStep && !tRecoveryStep && <>
              <div style={{ ...anim(60), marginBottom: 28, textAlign: 'center' }}>
                <img src={LOGO_SRC} alt="LKPS" style={{ width: 56, height: 56, objectFit: 'contain', marginBottom: 14, display: 'block', margin: '0 auto 14px', filter: 'drop-shadow(0 4px 12px rgba(25,96,163,0.2))' }} onError={e => e.target.style.display = 'none'} />
                {!isStaff ? <>
                  <h2 style={{ fontSize: 24, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em', margin: '0 0 5px', fontFamily: 'Manrope,sans-serif' }}>Parent Login</h2>
                  <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Sign in to track your child's progress</p>
                </> : <>
                  <h2 style={{ fontSize: 24, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em', margin: '0 0 5px', fontFamily: 'Manrope,sans-serif' }}>Staff Login</h2>
                  <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>{role === 'admin' ? 'Admin portal access' : 'Teacher portal access'}</p>
                </>}
              </div>

              {err && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fef2f2', color: '#dc2626', borderRadius: 12, padding: '11px 14px', fontSize: 12, fontWeight: 600, marginBottom: 16, border: '1px solid #fecaca', animation: 'lp-shake 400ms ease,lp-slidein 300ms ease' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16, flexShrink: 0 }}>error</span>{err}
                </div>
              )}

              {isStaff && (
                <div style={{ ...anim(100), display: 'flex', gap: 8, marginBottom: 20, background: '#f8fafc', borderRadius: 12, padding: 4 }}>
                  {[{ id: 'teacher', label: 'Teacher' }, { id: 'admin', label: 'Admin' }].map(r => (
                    <button key={r.id} onClick={() => { setRole(r.id); setErr(''); setU(''); setP(''); }}
                      style={{
                        flex: 1, padding: '9px', borderRadius: 9, border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 180ms',
                        background: role === r.id ? '#fff' : 'transparent',
                        color: role === r.id ? '#1960a3' : '#94a3b8',
                        boxShadow: role === r.id ? '0 1px 6px rgba(0,0,0,0.08)' : 'none',
                      }}>
                      {r.label}
                    </button>
                  ))}
                </div>
              )}

              <div style={{ ...anim(140), display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 7, letterSpacing: '0.01em' }}>
                    {role === 'parent' ? 'Parent Username' : role === 'admin' ? 'Admin Username' : 'Teacher Username'}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <span className="material-symbols-outlined" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: '#94a3b8', pointerEvents: 'none' }}>person</span>
                    <input value={u} onChange={e => setU(e.target.value)}
                      placeholder={role === 'parent' ? 'Enter parent username' : role === 'admin' ? 'Enter admin username' : 'Enter teacher username'}
                      autoComplete="off" onKeyDown={e => e.key === 'Enter' && document.getElementById('lp-pw').focus()}
                      className="lp-input"
                      style={{ width: '100%', paddingLeft: 42, paddingRight: 14, paddingTop: 12, paddingBottom: 12, borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 13, color: '#0f172a', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 7, letterSpacing: '0.01em' }}>Password</label>
                  <div style={{ position: 'relative' }}>
                    <span className="material-symbols-outlined" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: '#94a3b8', pointerEvents: 'none' }}>lock</span>
                    <input id="lp-pw" value={p} onChange={e => setP(e.target.value)} type={showPw ? 'text' : 'password'} placeholder="Enter password"
                      onKeyDown={e => e.key === 'Enter' && signin()}
                      className="lp-input"
                      style={{ width: '100%', paddingLeft: 42, paddingRight: 44, paddingTop: 12, paddingBottom: 12, borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 13, color: '#0f172a', outline: 'none', boxSizing: 'border-box' }} />
                    <button onClick={() => setShowPw(v => !v)} tabIndex={-1}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2, display: 'flex', alignItems: 'center' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{showPw ? 'visibility_off' : 'visibility'}</span>
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ ...anim(170), marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 7, letterSpacing: '0.01em' }}>Verification</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f1f5f9', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '10px 16px', fontWeight: 800, fontSize: 15, color: '#0f172a', letterSpacing: '0.05em', flexShrink: 0, fontFamily: 'monospace' }}>
                    {captcha.q} = ?
                  </div>
                  <input value={captchaVal} onChange={e => setCaptchaVal(e.target.value)} placeholder="Answer" onKeyDown={e => e.key === 'Enter' && signin()}
                    style={{ flex: 1, padding: '11px 14px', borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 13, color: '#0f172a', outline: 'none', boxSizing: 'border-box' }} />
                  <button onClick={refreshCaptcha} title="Refresh" tabIndex={-1}
                    style={{ padding: '10px', borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#64748b', flexShrink: 0 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>refresh</span>
                  </button>
                </div>
              </div>

              <div style={anim(200)}>
                <button onClick={signin} disabled={loading} className="lp-btn-main"
                  style={{
                    width: '100%', padding: '14px', borderRadius: 14, border: 'none',
                    background: isStaff ? 'linear-gradient(135deg,#1e293b,#334155)' : 'linear-gradient(135deg,#002045,#1960a3)',
                    color: '#fff', fontWeight: 800, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    boxShadow: isStaff ? '0 4px 20px rgba(30,41,59,0.3)' : '0 4px 20px rgba(0,32,69,0.28)',
                    transition: 'all 200ms', fontFamily: 'Manrope,sans-serif', opacity: loading ? 0.8 : 1
                  }}>
                  {loading
                    ? <><span className="material-symbols-outlined" style={{ fontSize: 18, animation: 'lp-spin 700ms linear infinite' }}>progress_activity</span>Signing in…</>
                    : <><span className="material-symbols-outlined" style={{ fontSize: 18 }}>login</span>Sign In</>}
                </button>
                {role === 'parent' && (
                  <div style={{ textAlign: 'center', marginTop: 10 }}>
                    <button onClick={() => { setRecoveryStep('choose'); setRecoveryErr(''); }}
                      style={{ background: 'none', border: 'none', color: '#1960a3', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                      Forgot username or password?
                    </button>
                  </div>
                )}
                {role === 'teacher' && (
                  <div style={{ textAlign: 'center', marginTop: 10 }}>
                    <button onClick={() => { setTRecoveryStep('choose'); setTRecoveryErr(''); setTRecoveryUser(u || ''); }}
                      style={{ background: 'none', border: 'none', color: '#475569', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                      Forgot username or password?
                    </button>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
                <span style={{ fontSize: 11, color: '#cbd5e1', fontWeight: 600, whiteSpace: 'nowrap' }}>{isStaff ? 'or' : 'Are you staff?'}</span>
                <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
              </div>
              <div style={{ marginTop: 12, textAlign: 'center' }}>
                {!isStaff ? (
                  <button onClick={() => { setRole('teacher'); setErr(''); setU(''); setP(''); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 180ms' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#1960a3'; e.currentTarget.style.color = '#1960a3'; e.currentTarget.style.background = '#eef4ff'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#475569'; e.currentTarget.style.background = '#f8fafc'; }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 15 }}>admin_panel_settings</span>
                    Staff Login (Admin / Teacher)
                  </button>
                ) : (
                  <button onClick={() => { setRole('parent'); setErr(''); setU(''); setP(''); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 180ms' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.color = '#7c3aed'; e.currentTarget.style.background = '#f5f3ff'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#475569'; e.currentTarget.style.background = '#f8fafc'; }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 15 }}>family_restroom</span>
                    Back to Parent Login
                  </button>
                )}
              </div>
            </>}
          </div>
        </div>

        <div style={{ padding: '14px 36px', borderTop: '1px solid #f1f5f9', textAlign: 'center', position: 'relative', zIndex: 2 }}>
          <span style={{ fontSize: 11, color: '#cbd5e1' }}>© 2026 Lord Krishna Public School, Mathura</span>
        </div>
      </div>
      <Toast />
    </div>
  );
} const NAV = [
  { sec: 'Main', items: [{ id: 'dash', icon: 'dashboard', label: 'Dashboard' }] },
  { sec: 'People', items: [{ id: 'adm', icon: 'how_to_reg', label: 'New Admissions' }, { id: 'stu', icon: 'group', label: 'Students' }, { id: 'tea', icon: 'badge', label: 'Staff' }, { id: 'satt', icon: 'co_present', label: 'Staff Attendance' }] },
  { sec: 'Academics', items: [{ id: 'cls', icon: 'class', label: 'Classes' }, { id: 'tt', icon: 'schedule', label: 'Timetable' }, { id: 'att', icon: 'fact_check', label: 'Attendance' }, { id: 'exam', icon: 'quiz', label: 'Exams & Grades' }, { id: 'cal', icon: 'calendar_month', label: 'Academic Calendar' }] },
  { sec: 'Finance', items: [{ id: 'fee', icon: 'payments', label: 'Finance' }] },
  { sec: 'Tools', items: [{ id: 'docs', icon: 'description', label: 'Documents' }, { id: 'rep', icon: 'summarize', label: 'Reports' }, { id: 'set', icon: 'settings', label: 'Settings' }] },
  { sec: 'Communication', items: [{ id: 'notices', icon: 'campaign', label: 'Notice Board' }, { id: 'msg', icon: 'mail', label: 'Messages' }] },
];


export default Login;
